import React from 'react';
import { Checkbox } from '../ui/Checkbox';
import { Badge } from '../ui/Badge';
import { Globe, Server, Database, Radio, Shield, AlertTriangle, AlertOctagon, AlertCircle } from 'lucide-react';
import { AssetActions } from './AssetActions';
import { TechnologyStack } from './TechnologyStack';
import * as Tooltip from '@radix-ui/react-tooltip';

interface TlsCertificate {
  sni?: string;
  host?: string;
  port?: string;
  cipher?: string;
  issuer_cn?: string;
  not_after?: string;
  not_before?: string;
  subject_cn?: string;
  tls_version?: string;
  probe_status?: boolean;
}

interface Asset {
  id: string;
  name: string;
  type: 'web' | 'server' | 'database' | 'network';
  status: 'active' | 'inactive' | 'maintenance' | 'vulnerable';
  riskScore: string;
  updatedAt: string;
  tags: string[];
  vulnerabilities: {
    critical: number;
    high: number;
    medium: number;
    low: number;
    total: number;
  };
  registrar?: string;
  registrationDate?: string | null;
  expirationDate?: string | null;
  tlsCertificate?: TlsCertificate | null;
}

interface AssetsTableProps {
  assets: Asset[];
  currentPage: number;
  onPageChange: (page: number) => void;
  totalAssets: number;
  itemsPerPage: number;
}

export const AssetsTable: React.FC<AssetsTableProps> = ({
  assets,
  currentPage,
  onPageChange,
  totalAssets,
  itemsPerPage,
}) => {
  const getAssetIcon = (type: string) => {
    switch (type) {
      case 'web': return Globe;
      case 'server': return Server;
      case 'database': return Database;
      case 'network': return Radio;
      default: return Globe;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-emerald-500 bg-emerald-500/10';
      case 'inactive': return 'text-gray-500 bg-gray-500/10';
      case 'maintenance': return 'text-yellow-500 bg-yellow-500/10';
      case 'vulnerable': return 'text-red-500 bg-red-500/10';
      default: return 'text-gray-500 bg-gray-500/10';
    }
  };

  const getVulnerabilityIcon = (count: number, type: 'critical' | 'high' | 'medium' | 'low') => {
    if (count === 0) return null;

    const icons = {
      critical: { icon: AlertOctagon, color: 'text-red-500' },
      high: { icon: AlertTriangle, color: 'text-orange-500' },
      medium: { icon: AlertCircle, color: 'text-yellow-500' },
      low: { icon: Shield, color: 'text-emerald-500' }
    };

    const { icon: Icon, color } = icons[type];
    
    return (
      <Tooltip.Provider>
        <Tooltip.Root>
          <Tooltip.Trigger asChild>
            <div className={`flex items-center space-x-1 ${color}`}>
              <Icon className="h-4 w-4" />
              <span className="text-xs font-medium">{count}</span>
            </div>
          </Tooltip.Trigger>
          <Tooltip.Portal>
            <Tooltip.Content
              className="px-3 py-1.5 rounded-lg bg-[#1A1F2E] border border-gray-800 text-xs text-white shadow-xl"
              sideOffset={5}
            >
              {count} {type} vulnerabilities
              <Tooltip.Arrow className="fill-[#1A1F2E]" />
            </Tooltip.Content>
          </Tooltip.Portal>
        </Tooltip.Root>
      </Tooltip.Provider>
    );
  };

  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalAssets);

  return (
    <div className="bg-[#0F1629] rounded-xl overflow-hidden border border-gray-800/50">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-800">
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Asset</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Technologies</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Vulnerabilities</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Risk Score</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Domain Details</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Changes Detected</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {assets.map((asset) => {
              const AssetIcon = getAssetIcon(asset.type);
              return (
                <tr key={asset.id} className="group hover:bg-gray-800/30">
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 rounded-lg bg-gray-800/30 group-hover:bg-gray-800/50 transition-colors">
                        <AssetIcon className="h-5 w-5 text-gray-400" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-white">{asset.name}</div>
                        <div className="flex items-center space-x-2 mt-1 flex-wrap">
                          {asset.tlsCertificate && (
                            <Tooltip.Provider>
                              <Tooltip.Root>
                                <Tooltip.Trigger asChild>
                                  <div className={`px-2 py-0.5 text-xs rounded-full ${asset.tlsCertificate.probe_status ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'} cursor-help`}>
                                    <div className="flex items-center space-x-1">
                                      <Shield className="h-3 w-3" />
                                      <span>{asset.tlsCertificate.probe_status ? 'TLS Valid' : 'TLS Invalid'}</span>
                                    </div>
                                  </div>
                                </Tooltip.Trigger>
                                <Tooltip.Portal>
                                  <Tooltip.Content
                                    className="px-3 py-1.5 rounded-lg bg-[#1A1F2E] border border-gray-800 text-xs text-white shadow-xl"
                                    sideOffset={5}
                                  >
                                    <div className="space-y-1">
                                      <div>Version: {asset.tlsCertificate?.tls_version ? asset.tlsCertificate.tls_version.toUpperCase() : 'Unknown'}</div>
                                      <div>Issuer: {asset.tlsCertificate?.issuer_cn || 'Unknown'}</div>
                                      {asset.tlsCertificate?.not_before && (
                                        <div>Valid from: {new Date(asset.tlsCertificate.not_before).toLocaleDateString()}</div>
                                      )}
                                      {asset.tlsCertificate?.not_after && (
                                        <div>Valid until: {new Date(asset.tlsCertificate.not_after).toLocaleDateString()}</div>
                                      )}
                                    </div>
                                    <Tooltip.Arrow className="fill-[#1A1F2E]" />
                                  </Tooltip.Content>
                                </Tooltip.Portal>
                              </Tooltip.Root>
                            </Tooltip.Provider>
                          )}
                          {asset.tags.map((tag, index) => (
                            <span
                              key={index}
                              className="px-2 py-0.5 text-xs rounded-full bg-gray-800/50 text-gray-400 hover:bg-gray-800 hover:text-gray-300 transition-colors cursor-pointer"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {/* Dummy tech stack - replace with actual data later */}
                    <TechnologyStack
                      technologies={[
                        {
                          name: 'React',
                          version: 'v18.2.0',
                          icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg',
                          color: 'blue'
                        },
                        {
                          name: 'Node.js',
                          version: 'v18.0.0',
                          icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg',
                          color: 'green'
                        },
                        {
                          name: 'JavaScript',
                          version: 'ES2022',
                          icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg',
                          color: 'yellow'
                        },
                        {
                          name: 'Nginx',
                          version: 'v1.24.0',
                          icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nginx/nginx-original.svg',
                          color: 'purple'
                        },
                        {
                          name: 'PostgreSQL',
                          version: 'v15',
                          icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/postgresql/postgresql-original.svg',
                          color: 'blue'
                        },
                        {
                          name: 'Redis',
                          version: 'v7.0',
                          icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/redis/redis-original.svg',
                          color: 'red'
                        }
                      ]}
                      maxVisible={3}
                    />
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      {getVulnerabilityIcon(asset.vulnerabilities.critical, 'critical')}
                      {getVulnerabilityIcon(asset.vulnerabilities.high, 'high')}
                      {getVulnerabilityIcon(asset.vulnerabilities.medium, 'medium')}
                      {getVulnerabilityIcon(asset.vulnerabilities.low, 'low')}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <Badge variant={
                      asset.riskScore?.toLowerCase() === 'high' ? 'high' : 
                      asset.riskScore?.toLowerCase() === 'medium' ? 'medium' : 
                      asset.riskScore?.toLowerCase() === 'low' ? 'low' : 
                      asset.riskScore?.toLowerCase() === 'critical' ? 'critical' : 
                      'default'
                    }>
                      {asset.riskScore?.toUpperCase() || 'Unknown'}
                    </Badge>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col space-y-2">
                      {asset.tlsCertificate && (
                        <Badge
                          className={`${asset.tlsCertificate.probe_status ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'}`}
                        >
                          <div className="flex items-center space-x-1">
                            <Shield className="h-3 w-3" />
                            <span>{asset.tlsCertificate.probe_status ? 'TLS Valid' : 'TLS Invalid'}</span>
                          </div>
                        </Badge>
                      )}
                      {asset.expirationDate && (
                        <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20">
                          <div className="flex items-center space-x-1">
                            <span>Expires: {new Date(asset.expirationDate).toLocaleDateString()}</span>
                          </div>
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <Tooltip.Provider>
                      <Tooltip.Root>
                        <Tooltip.Trigger asChild>
                          <div className="text-sm text-gray-300 cursor-help">
                            {asset.updatedAt}
                          </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                          <Tooltip.Content
                            className="px-3 py-1.5 rounded-lg bg-[#1A1F2E] border border-gray-800 text-xs text-white shadow-xl"
                            sideOffset={5}
                          >
                            <div className="space-y-1">
                              <div>Changes Detected {asset.updatedAt}</div>
                              {asset.registrar && <div>Registrar: {asset.registrar}</div>}
                              {asset.registrationDate && <div>Registered: {asset.registrationDate}</div>}
                              {asset.expirationDate && <div>Expires: {asset.expirationDate}</div>}
                            </div>
                            <Tooltip.Arrow className="fill-[#1A1F2E]" />
                          </Tooltip.Content>
                        </Tooltip.Portal>
                      </Tooltip.Root>
                    </Tooltip.Provider>
                  </td>
                  <td className="px-6 py-4">
                    <AssetActions assetId={asset.id} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="px-6 py-4 border-t border-gray-800 flex items-center justify-between bg-[#0A0F1C]">
        <div className="text-sm text-gray-400">
          Showing <span className="font-medium">{startIndex}</span> to{' '}
          <span className="font-medium">{endIndex}</span> of{' '}
          <span className="font-medium">{totalAssets}</span> assets
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-3 py-1 rounded-lg bg-gray-800/50 text-gray-300 hover:bg-gray-800/80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Previous
          </button>
          {totalAssets > 0 && (
            <div className="flex items-center space-x-2">
              {currentPage > 2 && (
                <button
                  onClick={() => onPageChange(1)}
                  className="px-3 py-1 rounded-lg bg-gray-800/50 text-gray-300 hover:bg-gray-800/80 transition-colors"
                >
                  1
                </button>
              )}
              {currentPage > 3 && (
                <span className="text-gray-500">...</span>
              )}
              {currentPage > 1 && (
                <button
                  onClick={() => onPageChange(currentPage - 1)}
                  className="px-3 py-1 rounded-lg bg-gray-800/50 text-gray-300 hover:bg-gray-800/80 transition-colors"
                >
                  {currentPage - 1}
                </button>
              )}
              <span className="px-3 py-1 rounded-lg bg-blue-500/10 text-blue-500">
                {currentPage}
              </span>
              {currentPage < Math.ceil(totalAssets / itemsPerPage) && (
                <button
                  onClick={() => onPageChange(currentPage + 1)}
                  className="px-3 py-1 rounded-lg bg-gray-800/50 text-gray-300 hover:bg-gray-800/80 transition-colors"
                >
                  {currentPage + 1}
                </button>
              )}
              {currentPage < Math.ceil(totalAssets / itemsPerPage) - 2 && (
                <span className="text-gray-500">...</span>
              )}
              {currentPage < Math.ceil(totalAssets / itemsPerPage) - 1 && (
                <button
                  onClick={() => onPageChange(Math.ceil(totalAssets / itemsPerPage))}
                  className="px-3 py-1 rounded-lg bg-gray-800/50 text-gray-300 hover:bg-gray-800/80 transition-colors"
                >
                  {Math.ceil(totalAssets / itemsPerPage)}
                </button>
              )}
            </div>
          )}
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={endIndex >= totalAssets}
            className="px-3 py-1 rounded-lg bg-gray-800/50 text-gray-300 hover:bg-gray-800/80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};